/**
 * Estado de la sesion de demostracion en el navegador.
 *
 * Dos cosas:
 *
 * 1. Saber si la sesion en curso es la demo. La fuente de verdad es el flag que
 *    el SERVIDOR firma en el token; esto es solo la copia local para pintar el
 *    banner y el aviso. Quien de verdad limita lo que se puede hacer es
 *    demoGuard en el backend, asi que manipular esto desde la consola no da
 *    ningun permiso extra.
 *
 * 2. Contar las acciones reales del visitante. No se cuentan minutos: quien ya
 *    ha creado un cliente, montado un servicio y emitido una factura ha visto el
 *    producto, y ESE es el momento de ofrecerle hablar. A los diez segundos de
 *    entrar, no.
 */

const CLAVE_SESION = 'guideme-demo';
const CLAVE_ACCIONES = 'guideme-demo-acciones';
const CLAVE_DESCARTADO = 'guideme-demo-captura-descartada';
const CLAVE_SRC = 'gm_demo_src';

export const EVENTO_ACCIONES = 'guideme-demo-accion';

/** El backend ha rechazado una escritura por ser la demo. Lleva el mensaje. */
export const EVENTO_VETO = 'guideme-demo-veto';

/** sessionStorage puede lanzar (modo privado, cookies bloqueadas). Nunca romper por esto. */
function leer(clave: string): string | null {
  try {
    return sessionStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribir(clave: string, valor: string): void {
  try {
    sessionStorage.setItem(clave, valor);
  } catch {
    /* sin almacenamiento, el contador se queda a cero y no pasa nada */
  }
}

export function marcaSesionDemo(esDemo: boolean): void {
  if (esDemo) escribir(CLAVE_SESION, '1');
  else {
    try {
      sessionStorage.removeItem(CLAVE_SESION);
      sessionStorage.removeItem(CLAVE_ACCIONES);
      sessionStorage.removeItem(CLAVE_DESCARTADO);
    } catch {
      /* nada que limpiar */
    }
  }
}

export function esSesionDemo(): boolean {
  return leer(CLAVE_SESION) === '1';
}

export function accionesHechas(): number {
  return Number(leer(CLAVE_ACCIONES) || 0);
}

export function capturaDescartada(): boolean {
  return leer(CLAVE_DESCARTADO) === '1';
}

export function descartaCaptura(): void {
  escribir(CLAVE_DESCARTADO, '1');
}

function apuntaAccion(): void {
  const total = accionesHechas() + 1;
  escribir(CLAVE_ACCIONES, String(total));
  window.dispatchEvent(new CustomEvent(EVENTO_ACCIONES, { detail: total }));
}

let envuelto = false;

/**
 * Envuelve window.fetch para contar las escrituras del visitante.
 *
 * El frontend llama a la API con fetch suelto desde cada pantalla, no con una
 * instancia central donde poner un interceptor, asi que se envuelve el fetch
 * global. Se instala UNA vez y solo cuenta:
 *   - peticiones a nuestra API,
 *   - que no sean GET,
 *   - que hayan salido bien,
 *   - y que no sean del propio flujo de demo (entrar o mandar el formulario).
 *
 * Fuera de la demo no se instala nada.
 */
export function instalaContadorDeAcciones(): void {
  if (envuelto || typeof window === 'undefined') return;
  envuelto = true;

  const original = window.fetch.bind(window);

  window.fetch = async (entrada: RequestInfo | URL, init?: RequestInit) => {
    const respuesta = await original(entrada, init);

    try {
      if (!esSesionDemo()) return respuesta;

      const metodo = (init?.method || (entrada instanceof Request ? entrada.method : 'GET')).toUpperCase();
      if (metodo === 'GET' || metodo === 'HEAD') return respuesta;

      // El backend ha vetado esta escritura (demoGuard). Se avisa desde aquí,
      // un único sitio, en vez de parchear cada botón de la aplicación: el
      // visitante no debería rellenar un formulario entero para enterarse.
      // Se clona antes de leer para no consumir el cuerpo que espera quien llamó.
      if (respuesta.status === 403) {
        respuesta.clone().json().then((cuerpo) => {
          if (cuerpo?.code === 'DEMO_SOLO_LECTURA') {
            window.dispatchEvent(new CustomEvent(EVENTO_VETO, { detail: cuerpo.message }));
          }
        }).catch(() => { /* no era JSON: no es lo nuestro */ });
        return respuesta;
      }

      if (!respuesta.ok) return respuesta;

      const url = typeof entrada === 'string' ? entrada
        : entrada instanceof URL ? entrada.toString()
        : entrada.url;
      if (!url.includes('/api/')) return respuesta;
      // Entrar en la demo y mandar el formulario no son "usar el producto".
      if (url.includes('/api/auth/') || url.includes('/api/demo/')) return respuesta;
      if (url.includes('/api/app-events')) return respuesta;

      apuntaAccion();
    } catch {
      /* contar es accesorio: jamas puede romper una peticion */
    }

    return respuesta;
  };
}

/**
 * URL de registro desde dentro de la demo. Conserva de qué botón de la web
 * salió el visitante (`src` de /demo) para que el alta quede atribuida.
 */
export function urlRegistroDesdeDemo(): string {
  const src = (leer(CLAVE_SRC) || 'directo').replace(/[^a-z0-9_-]/gi, '').slice(0, 40);
  return `/signup?utm_source=demo&utm_medium=demo&utm_campaign=${encodeURIComponent(src)}`;
}
